
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Video, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface VideoTutorial {
  id: string;
  title: string;
  description: string;
  youtube_url: string;
  duration: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

const AdminVideoManagement = () => {
  const [editingVideo, setEditingVideo] = useState<VideoTutorial | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    duration: '',
    category: ''
  });

  const queryClient = useQueryClient();

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['video-tutorials'],
    queryFn: async () => {
      // First check admin access
      const { data: user } = await supabase.auth.getUser();
      if (!user.user?.email) {
        throw new Error('User not authenticated');
      }

      const { data: isAdmin } = await supabase.rpc('check_admin_simple', {
        user_email: user.user.email
      });

      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      // Now fetch video tutorials using raw SQL query
      const { data, error } = await supabase
        .from('video_tutorials')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as VideoTutorial[];
    }
  });

  const createVideoMutation = useMutation({
    mutationFn: async (videoData: Omit<VideoTutorial, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('video_tutorials')
        .insert([videoData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video-tutorials'] });
      toast.success('Video tutorial created successfully');
      resetForm();
    },
    onError: (error) => {
      console.error('Error creating video:', error);
      toast.error('Failed to create video tutorial');
    }
  });

  const updateVideoMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<VideoTutorial> & { id: string }) => {
      const { data, error } = await supabase
        .from('video_tutorials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video-tutorials'] });
      toast.success('Video tutorial updated successfully');
      setEditingVideo(null);
    },
    onError: (error) => {
      console.error('Error updating video:', error);
      toast.error('Failed to update video tutorial');
    }
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('video_tutorials')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video-tutorials'] });
      toast.success('Video tutorial deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video tutorial');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      youtube_url: '',
      duration: '',
      category: ''
    });
    setIsCreating(false);
    setEditingVideo(null);
  };

  const handleCreate = () => {
    if (!formData.title || !formData.youtube_url) {
      toast.error('Title and YouTube URL are required');
      return;
    }

    createVideoMutation.mutate({
      ...formData,
      is_active: true
    });
  };

  const handleUpdate = () => {
    if (!editingVideo) return;

    updateVideoMutation.mutate({
      id: editingVideo.id,
      ...formData
    });
  };

  const startEdit = (video: VideoTutorial) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtube_url: video.youtube_url,
      duration: video.duration,
      category: video.category
    });
    setIsCreating(false);
  };

  const toggleVideoStatus = (video: VideoTutorial) => {
    updateVideoMutation.mutate({
      id: video.id,
      is_active: !video.is_active
    });
  };

  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  if (isLoading) {
    return <div className="text-white">Loading video tutorials...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Video Tutorial Management</h2>
        <Button
          onClick={() => setIsCreating(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video Tutorial
        </Button>
      </div>

      {(isCreating || editingVideo) && (
        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardHeader>
            <CardTitle className="text-white">
              {isCreating ? 'Create New Video Tutorial' : 'Edit Video Tutorial'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter video title"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter video description"
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">YouTube URL</label>
              <Input
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Duration</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 5:30"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Beginner, Advanced"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={isCreating ? handleCreate : handleUpdate}
                disabled={createVideoMutation.isPending || updateVideoMutation.isPending}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {isCreating ? 'Create' : 'Update'}
              </Button>
              <Button
                onClick={resetForm}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {videos.map((video) => {
          const youtubeId = getYouTubeId(video.youtube_url);
          return (
            <Card key={video.id} className="bg-white/10 backdrop-blur-md border-white/20">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex space-x-4 flex-1">
                    <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-700">
                      {youtubeId ? (
                        <img
                          src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">{video.title}</h3>
                        <Badge variant={video.is_active ? "default" : "secondary"}>
                          {video.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="border-blue-500/30 text-blue-300">
                          {video.category}
                        </Badge>
                      </div>
                      <p className="text-gray-300 mb-2">{video.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span>Duration: {video.duration}</span>
                        <a
                          href={video.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View on YouTube
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-300">Active</span>
                      <Switch
                        checked={video.is_active}
                        onCheckedChange={() => toggleVideoStatus(video)}
                      />
                    </div>
                    <Button
                      onClick={() => startEdit(video)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => deleteVideoMutation.mutate(video.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {videos.length === 0 && (
        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardContent className="text-center py-12">
            <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Video Tutorials</h3>
            <p className="text-gray-300 mb-4">Get started by adding your first video tutorial.</p>
            <Button
              onClick={() => setIsCreating(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Video Tutorial
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminVideoManagement;
